// src/features/ai/AiChat.tsx
import React, { useState, useRef, useEffect } from 'react';
import {
    Box,
    TextField,
    Button,
    Paper,
    Typography,
    IconButton,
    Collapse,
    Divider,
    CircularProgress,
    Alert,
    Chip,
    Stack,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { sendChatMessage, ChatMessage, ChatResponse } from '../../api/aiChatService';
import { v4 as uuidv4 } from 'uuid';

const AiChat: React.FC = () => {
    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [sessionId, setSessionId] = useState(uuidv4());
    const [searchK, setSearchK] = useState(20);
    const [selectedModel, setSelectedModel] = useState('gpt-4o-mini');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [expandedMessageIndex, setExpandedMessageIndex] = useState<number | null>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleClearChat = () => {
        setMessages([]);
        setSessionId(uuidv4());
        setError(null);
    };

    const handleSendMessage = async () => {
        if (!inputValue.trim() || isLoading) return;

        const userMessage: ChatMessage = {
            role: 'user',
            content: inputValue,
            timestamp: new Date().toISOString(),
        };

        setMessages((prev) => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setError(null);

        try {
            const response: ChatResponse = await sendChatMessage({
                session_id: sessionId,
                question: inputValue,
                chat_room_id: 'ab846ce0-2ef3-459e-8e2c-507fc1faaa85',
                document_id: 'cc846ce0-2ef3-459e-8e2c-507fc1faaa85',
                search_k: searchK,
                include_sources: true,
                max_iterations: 10,
                model: selectedModel,
            });

            const assistantMessage: ChatMessage = {
                role: 'assistant',
                content: response.answer,
                timestamp: response.timestamp,
                details: {
                    reasoning: response.reasoning,
                    steps: response.steps,
                    tools_used: response.tools_used,
                    sources: response.sources,
                    api_calls: response.api_calls,
                    model: response.model,
                    processing_time_ms: response.processing_time_ms,
                    iterations: response.iterations,
                },
            };

            setMessages((prev) => [...prev, assistantMessage]);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || '發送訊息時發生錯誤');
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const toggleExpandMessage = (index: number) => {
        setExpandedMessageIndex(expandedMessageIndex === index ? null : index);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '70vh' }}>
            {/* Top Controls */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                    mb: 2,
                }}
            >
                <FormControl size="small" sx={{ minWidth: 150 }}>
                    <InputLabel id="model-select-label">模型</InputLabel>
                    <Select
                        labelId="model-select-label"
                        value={selectedModel}
                        label="模型"
                        onChange={(e) => setSelectedModel(e.target.value)}
                    >
                        <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                        <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                        <MenuItem value="gpt-5-mini">gpt-5-mini</MenuItem>
                        {/*<MenuItem value="gpt-5">gpt-5</MenuItem>*/}
                    </Select>
                </FormControl>
                <TextField
                    label="Search K 值"
                    type="number"
                    value={searchK}
                    onChange={(e) => setSearchK(Number(e.target.value))}
                    size="small"
                    sx={{ width: 120 }}
                    inputProps={{ min: 1, max: 100 }}
                />
                <Button
                    variant="outlined"
                    startIcon={<DeleteIcon />}
                    onClick={handleClearChat}
                    color="error"
                >
                    清理對話
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
                    {error}
                </Alert>
            )}

            {/* Chat Messages */}
            <Paper
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 2,
                    mb: 2,
                    backgroundColor: 'background.default',
                }}
            >
                {messages.length === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                        }}
                    >
                        <Typography variant="body1" color="text.secondary">
                            開始與 AI 對話...
                        </Typography>
                    </Box>
                )}

                {messages.map((message, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        {message.role === 'user' ? (
                            <Paper
                                sx={{
                                    p: 2,
                                    backgroundColor: 'primary.main',
                                    color: 'primary.contrastText',
                                    maxWidth: '80%',
                                    ml: 'auto',
                                }}
                            >
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {message.content}
                                </Typography>
                                <Typography variant="caption" sx={{ mt: 1, display: 'block', opacity: 0.8 }}>
                                    {new Date(message.timestamp).toLocaleString('zh-TW')}
                                </Typography>
                            </Paper>
                        ) : (
                            <Paper
                                sx={{
                                    p: 2,
                                    backgroundColor: 'background.paper',
                                    maxWidth: '80%',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 1 }}>
                                    {message.content}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                                    {new Date(message.timestamp).toLocaleString('zh-TW')}
                                </Typography>

                                {/* Expandable Details */}
                                {message.details && (
                                    <>
                                        <Divider sx={{ my: 1 }} />
                                        <Button
                                            size="small"
                                            onClick={() => toggleExpandMessage(index)}
                                            endIcon={
                                                expandedMessageIndex === index ? (
                                                    <ExpandLessIcon />
                                                ) : (
                                                    <ExpandMoreIcon />
                                                )
                                            }
                                        >
                                            {expandedMessageIndex === index ? '隱藏詳細資訊' : '顯示詳細資訊'}
                                        </Button>

                                        <Collapse in={expandedMessageIndex === index}>
                                            <Box sx={{ mt: 2 }}>
                                                {/* Processing Time */}
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    處理時間: {message.details.processing_time_ms} ms
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary">
                                                    模型: {message.details.model}
                                                </Typography>
                                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                                                    迭代次數: {message.details.iterations}
                                                </Typography>

                                                {/* Tools Used */}
                                                {message.details.tools_used && message.details.tools_used.length > 0 && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                            使用的工具:
                                                        </Typography>
                                                        <Stack direction="row" spacing={1} flexWrap="wrap">
                                                            {message.details.tools_used.map((tool, idx) => (
                                                                <Chip key={idx} label={tool} size="small" />
                                                            ))}
                                                        </Stack>
                                                    </Box>
                                                )}

                                                {/* Reasoning */}
                                                <Box sx={{ mb: 2 }}>
                                                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                        推理過程:
                                                    </Typography>
                                                    <Paper sx={{ p: 1, backgroundColor: 'action.hover' }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{ whiteSpace: 'pre-wrap' }}
                                                        >
                                                            {message.details.reasoning}
                                                        </Typography>
                                                    </Paper>
                                                </Box>

                                                {/* Steps */}
                                                {message.details.steps && message.details.steps.length > 0 && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                            執行步驟:
                                                        </Typography>
                                                        {message.details.steps.map((step, idx) => (
                                                            <Paper
                                                                key={idx}
                                                                sx={{ p: 1.5, mb: 1, backgroundColor: 'action.hover' }}
                                                            >
                                                                <Typography variant="body2" fontWeight="bold">
                                                                    步驟 {step.step_number}: {step.node_name}
                                                                </Typography>
                                                                <Typography variant="body2" color="text.secondary">
                                                                    動作: {step.action}
                                                                </Typography>
                                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                                                    {step.description}
                                                                </Typography>
                                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                                    {new Date(step.timestamp).toLocaleString('zh-TW')}
                                                                </Typography>
                                                                
                                                                {/* Tool Calls */}
                                                                {step.tool_calls && step.tool_calls.length > 0 && (
                                                                    <Box sx={{ mt: 1, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                                                                        <Typography variant="caption" fontWeight="bold" color="text.secondary">
                                                                            工具呼叫:
                                                                        </Typography>
                                                                        {step.tool_calls.map((toolCall, tcIdx) => (
                                                                            <Box key={tcIdx} sx={{ mt: 0.5 }}>
                                                                                <Typography variant="caption" color={toolCall.success ? 'success.main' : 'error.main'}>
                                                                                    {toolCall.success ? '✓' : '✗'} {toolCall.tool_name}
                                                                                </Typography>
                                                                                {toolCall.error && (
                                                                                    <Typography variant="caption" color="error" sx={{ display: 'block', ml: 2 }}>
                                                                                        錯誤: {toolCall.error}
                                                                                    </Typography>
                                                                                )}
                                                                            </Box>
                                                                        ))}
                                                                    </Box>
                                                                )}
                                                            </Paper>
                                                        ))}
                                                    </Box>
                                                )}

                                                {/* Sources */}
                                                {message.details.sources && message.details.sources.length > 0 && (
                                                    <Box sx={{ mb: 2 }}>
                                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                            來源 ({message.details.sources.length}):
                                                        </Typography>
                                                        {[...message.details.sources]
                                                            .sort((a, b) => b.similarity_score - a.similarity_score)
                                                            .map((source, idx) => (
                                                                <Paper
                                                                    key={idx}
                                                                    sx={{ p: 1.5, mb: 1, backgroundColor: 'action.hover' }}
                                                                >
                                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                                                        <Typography variant="caption" color="text.secondary">
                                                                            #{source.index} | {source.category}
                                                                        </Typography>
                                                                        <Chip
                                                                            label={`相似度: ${source.similarity_score.toFixed(4)}`}
                                                                            size="small"
                                                                            color="primary"
                                                                            variant="outlined"
                                                                        />
                                                                    </Box>
                                                                    <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
                                                                        {source.content}
                                                                    </Typography>
                                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                                        Document ID: {source.document_id}
                                                                    </Typography>
                                                                </Paper>
                                                            ))}
                                                    </Box>
                                                )}

                                                {/* API Calls */}
                                                {message.details.api_calls && Object.keys(message.details.api_calls).length > 0 && (
                                                    <Box>
                                                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                                                            API 呼叫:
                                                        </Typography>
                                                        <Paper sx={{ p: 1.5, backgroundColor: 'action.hover' }}>
                                                            <Typography
                                                                variant="body2"
                                                                component="pre"
                                                                sx={{
                                                                    whiteSpace: 'pre-wrap',
                                                                    wordBreak: 'break-word',
                                                                    fontFamily: 'monospace',
                                                                    fontSize: '0.75rem',
                                                                }}
                                                            >
                                                                {JSON.stringify(message.details.api_calls, null, 2)}
                                                            </Typography>
                                                        </Paper>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Collapse>
                                    </>
                                )}
                            </Paper>
                        )}
                    </Box>
                ))}

                {isLoading && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                            AI 正在思考...
                        </Typography>
                    </Box>
                )}

                <div ref={messagesEndRef} />
            </Paper>

            {/* Input Area */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="輸入您的問題..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyPress={handleKeyPress}
                    disabled={isLoading}
                />
                <Button
                    variant="contained"
                    endIcon={<SendIcon />}
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    sx={{ minWidth: 100 }}
                >
                    發送
                </Button>
            </Box>
        </Box>
    );
};

export default AiChat;



